if (!customElements.get('bulk-order')) {
  customElements.define(
    'bulk-order',
    class BulkOrder extends HTMLElement {
      constructor() {
        super();

        this.addToCartButton = this.querySelector('.btn-add-to-cart');
        this.loadMoreButton = this.querySelector('.btn-load-more');
        this.loadMoreButtonWrapper = this.querySelector('.load-more-button-wrapper');
        this.bulkOrderTable = this.querySelector('.bulk-order-table');
        this.loadingSpinnerWrapper = this.querySelector('.bulk-order-loading-spinner-wrapper');
        
        this.currentPage = 1;
        this.totalProducts = parseInt(this.dataset.totalProducts);
        this.productsPerPage = parseInt(this.dataset.productsPerPage);
        this.paginationType = this.dataset.paginationType;
        
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.hideErrors = this.dataset.hideErrors === 'true';

        // Add to cart button event
        if (this.addToCartButton) {
          this.addToCartButton.addEventListener('click', this.onSubmitHandler.bind(this));
        }
        
        // Load more button event
        if (this.paginationType === 'load_more' && this.loadMoreButton) {
          this.loadMoreButton.addEventListener('click', () => this.fetchProducts());
        }
        
        // Infinite scroll event
        if (this.paginationType === 'infinite_scroll') {
          this.isFetching = false;
          window.addEventListener('scroll', this.onScrollHandler.bind(this));
        }
      }

      onScrollHandler() {
        const scrollHeight = this.bulkOrderTable.scrollHeight;
        const { scrollTop, clientHeight } = document.documentElement;
        if (scrollTop + clientHeight >= scrollHeight - 200 && 
            !this.isFetching && 
            this.currentPage * this.productsPerPage < this.totalProducts) {
          console.log('fetching products');
          this.isFetching = true;
          this.fetchProducts().then(() => {
            this.isFetching = false;
          });
        }
      }

      async fetchProducts() {
        this.loadingSpinnerWrapper.classList.remove('hidden');
        if (this.loadMoreButton) {
          this.loadMoreButton.classList.add('loading');
          this.loadMoreButton.querySelector('.loading__spinner')?.classList.remove('hidden');
        }
        
        this.currentPage++;

        try {
          const response = await fetch(`${window.location.pathname}?page=${this.currentPage}`);
          const data = await response.text();
          const parsedData = new DOMParser().parseFromString(data, 'text/html');
          const newProducts = parsedData.querySelectorAll('.bulk-order-product');
          
          newProducts.forEach(product => {
            this.bulkOrderTable.appendChild(product.cloneNode(true));
          });

          // Hide load more button if all products loaded
          if (this.currentPage * this.productsPerPage >= this.totalProducts) {
            if (this.loadMoreButtonWrapper) {
              this.loadMoreButtonWrapper.style.display = 'none';
            }
          }
        } catch (error) {
          console.error('Error fetching products:', error);
        } finally {
          if (this.loadMoreButton) {
            this.loadMoreButton.classList.remove('loading');
            this.loadMoreButton.querySelector('.loading__spinner')?.classList.add('hidden');
          }
          this.loadingSpinnerWrapper.classList.add('hidden');
        }
      }
      
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.addToCartButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        const variantsData = this.getVariantsData();
        if (variantsData.length === 0) return;

        this.addToCartButton.setAttribute('aria-disabled', true);
        this.addToCartButton.classList.add('loading');

        const formData = {
          items: variantsData.map(item => ({
            quantity: parseInt(item.quantity),
            id: item.variantId
          }))
        };

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        
        // Add sections to request if cart drawer/notification exists
        if (this.cart) {
          formData.sections = this.cart.getSectionsToRender().map((section) => section.id);
          formData.sections_url = window.location.pathname;
        }
        
        config.body = JSON.stringify(formData);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'bulk-order',
                productVariantId: variantsData.map(item => item.variantId),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });

              const soldOutMessage = this.addToCartButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.addToCartButton.setAttribute('aria-disabled', true);
                this.addToCartButton.querySelector('span').classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'bulk-order',
                cartData: response,
              });
            }

            this.error = false;
            
            if (this.cart) {
              this.cart.renderContents(response);
            }

            // Reset all quantities to 0
            this.resetQuantities();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.addToCartButton.removeAttribute('aria-disabled');
            this.addToCartButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
          });
      }

      getVariantsData() {
        const variantsQuantitySelectors = this.querySelectorAll('quantity-input .quantity__input');
        let variantsQuantityData = [];
        
        variantsQuantitySelectors.forEach(selector => {
          const quantity = selector.value;
          const variantId = selector.dataset.quantityVariantId;
          if (quantity > 0) {
            variantsQuantityData.push({ quantity, variantId });
          }
        });
        
        return variantsQuantityData;
      }

      resetQuantities() {
        const variantsQuantitySelectors = this.querySelectorAll('quantity-input .quantity__input');
        variantsQuantitySelectors.forEach(selector => {
          selector.value = 0;
        });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.bulk-order-error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.bulk-order-error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}